import {
  DOCTOR_NOTICE_BODY_MAX,
  DOCTOR_NOTICE_FIELD_LABEL,
  type DoctorNoticeConfirmations,
} from "@care-by-ella/contracts";
import { doctorNoticeClaimErrors } from "@/lib/doctor-notice";

// TES-1201 — "Läkarens besked till patienten · visas under Din vård": ONE
// editable text, prefilled with Emelie's headings from the decision, the
// chosen completions and the confirmed Webdoc actions. Shared by the care
// meeting's Webdoc step and the treatment decision, so the field reads the
// same wherever the doctor writes to the patient.
//
// `value` is null while the doctor has not typed in it: the field then shows
// `text` and follows the prefill (doctorNoticeText), or stays empty when
// `startEmpty` (a re-documentation sends no new notice unless he asks for
// one). The caller holds the state and sends `text`.
export function DoctorNoticeField({
  id,
  text,
  value,
  prefill,
  onChange,
  confirmed,
  disabled,
  error,
  startEmpty = false,
}: {
  id?: string;
  text: string;
  value: string | null;
  prefill: string;
  onChange: (next: string | null) => void;
  confirmed: DoctorNoticeConfirmations;
  disabled?: boolean;
  error?: string;
  startEmpty?: boolean;
}) {
  const claims = doctorNoticeClaimErrors(text, confirmed);
  // "Fyll i förslaget" on an empty re-documentation; "Återställ förslaget"
  // once he has changed the text.
  const fill = value === null && startEmpty && prefill !== "";
  const reset = value !== null && value !== prefill;
  return (
    <div id={id} className="flex flex-col gap-xs text-sm">
      <label htmlFor={id ? `${id}-text` : undefined} className="font-semibold">
        {DOCTOR_NOTICE_FIELD_LABEL}
      </label>
      <textarea
        id={id ? `${id}-text` : undefined}
        aria-invalid={claims.length > 0 || error ? true : undefined}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        rows={Math.min(16, Math.max(6, text.split("\n").length + 1))}
        maxLength={DOCTOR_NOTICE_BODY_MAX}
        disabled={disabled}
        className="rounded-md border border-surfaceMuted p-sm text-sm focus:border-brand focus:outline-none"
      />
      <span className="text-xs text-textSecondary">
        Förifyllt utifrån beslutet, valda kompletteringar och bekräftade
        åtgärder. Skriv din medicinska bedömning under rubriken. Rubriker utan
        innehåll tas inte med. Patienten kan inte svara på beskedet.
        {startEmpty ? " Lämnar du fältet tomt skickas inget nytt besked." : ""}
      </span>
      {fill || reset ? (
        <button
          type="button"
          onClick={() => onChange(startEmpty ? prefill : null)}
          disabled={disabled}
          className="self-start text-xs font-semibold text-brand hover:underline disabled:opacity-50"
        >
          {fill ? "Fyll i förslaget" : "Återställ förslaget"}
        </button>
      ) : null}
      {claims.map((message) => (
        <p key={message} className="text-xs text-danger">
          {message}
        </p>
      ))}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
    </div>
  );
}
